import React from 'react';
import { Link } from 'wouter';

interface LogoProps {
  variant?: 'simple' | 'complete';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showText?: boolean;
  linkTo?: string;
}

/**
 * Componente Logo reutilizable para Statemia
 * 
 * CARACTERÍSTICAS:
 * - Fallback automático a gradiente si la imagen no carga
 * - Responsive y escalable
 * - Soporte para diferentes variantes (simple/completo)
 * - Integración con el sistema de navegación
 * 
 * EJEMPLOS DE USO:
 * 
 * // Header principal
 * <LogoComplete size="md" linkTo="/" />
 * 
 * // Footer
 * <LogoComplete size="lg" />
 * 
 * // Icono en sidebar
 * <LogoSimple size="sm" />
 * 
 * // Solo icono sin texto
 * <LogoIcon size="lg" />
 * 
 * // Logo personalizado con clases CSS
 * <Logo variant="complete" size="xl" className="my-custom-class" />
 * 
 * // Sin enlace
 * <LogoComplete size="md" linkTo="" />
 * 
 * TAMAÑOS DISPONIBLES:
 * - sm: 24x24px (h-6 w-6)
 * - md: 32x32px (h-8 w-8) 
 * - lg: 40x40px (h-10 w-10)
 * - xl: 48x48px (h-12 w-12)
 */
export const Logo: React.FC<LogoProps> = ({
  variant = 'complete',
  size = 'md',
  className = '',
  showText = true,
  linkTo = '/'
}) => {
  // Size mappings
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
    xl: 'h-12 w-12'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl'
  };

  const logoImage = variant === 'simple' ? '/images/logo-simple.png' : '/images/logo-complete.png';
  const logoAlt = 'Statemia';

  const LogoContent = () => (
    <div className={`flex items-center ${className}`}>
      <img
        src={logoImage}
        alt={logoAlt}
        className={`${variant === 'complete' ? 'h-full w-auto' : sizeClasses[size]} object-contain`}
        width={variant === 'complete' ? 200 : 40}
        height={variant === 'complete' ? 40 : 40}
        onError={(e) => {
          // Fallback to gradient if image fails to load
          const target = e.currentTarget;
          target.style.display = 'none';
          const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) {
              fallback.style.display = 'flex';
            }
          }
        }
      />
      {/* Fallback gradient logo */}
      <div 
        className={`${variant === 'complete' ? 'h-full w-auto' : sizeClasses[size]} bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center ${variant === 'complete' ? 'mr-3' : ''}`}
        style={{ display: 'none' }}
      >
        <span className="text-white font-bold text-lg">S</span>
      </div>
      
      {showText && variant === 'complete' && (
        <span className={`font-semibold text-slate-900 dark:text-white ml-3 ${textSizeClasses[size]}`}>
          Statemia
        </span>
      )}
    </div>
  );

  if (linkTo) {
    return (
      <Link href={linkTo} className="hover:opacity-80 transition-opacity">
        <LogoContent />
      </Link>
    );
  }

  return <LogoContent />;
};

// Convenience components for common use cases
export const LogoSimple: React.FC<Omit<LogoProps, 'variant'> & { size?: 'sm' | 'md' | 'lg' }> = (props) => (
  <Logo variant="simple" {...props} />
);

export const LogoComplete: React.FC<Omit<LogoProps, 'variant'> & { size?: 'sm' | 'md' | 'lg' | 'xl' }> = (props) => (
  <Logo variant="complete" {...props} />
);

export const LogoIcon: React.FC<Omit<LogoProps, 'variant' | 'showText'> & { size?: 'sm' | 'md' | 'lg' }> = (props) => (
  <Logo variant="simple" showText={false} {...props} />
); 